import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAutoTranslation } from 'src/hooks/useTranslate';

/**
 * A keyboard key, drawn as a key cap.
 *
 * Lifted out of `ShortcutsHelp` when TGCLOUD-1642's footnote was picked up: the same
 * cap now appears on the context menu's Add Relative rows and on the sidebar's Add
 * Relative tiles, and three hand-drawn versions of a key cap is how they come to
 * disagree. The keys themselves come from `ADD_RELATIVE_KEYS` for the same reason.
 *
 * `size` exists because the overlay and the menu need different weights. `'md'` is
 * the overlay's original geometry, unchanged to the pixel — it sits alone in a
 * roomy dialog row. `'sm'` is for a menu row or a tile, where a 24px cap beside a
 * 14px label reads as the loudest thing in the row rather than a hint.
 */

const SIZES = {
  md: { minWidth: 24, height: 24, fontSize: 12, px: 0.85, radius: '6px' },
  sm: { minWidth: 19, height: 19, fontSize: 11, px: 0.6, radius: '5px' }
};

export const Kbd = ({ children, size = 'md' }) => {
  const theme = useTheme();
  const s = SIZES[size] || SIZES.md;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: s.minWidth,
        height: s.height,
        px: s.px,
        borderRadius: s.radius,
        border: '1px solid',
        borderColor: theme.palette.divider,
        // The 2px bottom edge is what makes it read as a physical key rather than
        // as a bordered label.
        borderBottomWidth: 2,
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        fontFamily: 'monospace',
        fontSize: s.fontSize,
        fontWeight: 600,
        lineHeight: 1,
        boxShadow:
          theme.palette.mode === 'light'
            ? '0 1px 0 rgba(0,0,0,0.06)'
            : '0 1px 0 rgba(0,0,0,0.4)'
      }}
    >
      {children}
    </Box>
  );
};

// A key named by a word (Shift, Ctrl, Delete, Esc, Scroll) is a word on screen,
// so it is translated. A letter, a symbol, an arrow or a function key (F2) is
// the same in every language and is shown as it is.
const isNamedKey = (k) => typeof k === 'string' && /^[A-Za-z]{2,}$/.test(k);

/** One or more caps joined by a "+", e.g. Shift + A. */
export const KeyCombo = ({ keys, size = 'md' }) => {
  const theme = useTheme();
  const t = useAutoTranslation();
  if (!keys?.length) return null;
  return (
    <Box
      aria-hidden="true"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
    >
      {keys.map((k, i) => (
        <React.Fragment key={`${k}-${i}`}>
          {i > 0 && (
            <Typography
              component="span"
              sx={{
                color: theme.palette.text.disabled,
                fontSize: size === 'sm' ? 11 : 12
              }}
            >
              +
            </Typography>
          )}
          <Kbd size={size}>{isNamedKey(k) ? t(k) : k}</Kbd>
        </React.Fragment>
      ))}
    </Box>
  );
};

export default KeyCombo;
